import { Intent, Tag, Tooltip } from "@blueprintjs/core";
import {
    ColDef,
    ColGroupDef,
    ICellRendererParams,
    RowClassRules
} from "ag-grid-community";
import { useMemo } from "react";
import { BaseAgGrid } from "../tables/base/BaseAgGrid";
import css from "./CompareTable.module.scss";
import {
    CompareColumn,
    CompareTableColumn,
    DeltaFormat,
    isRelativeDeltaSignificant,
    MergedRow,
    TextCompareColumn
} from "./comparisons";

type CompareTableProps<TRow> = {
    merged: ReadonlyArray<MergedRow<TRow>>;
    columns: ReadonlyArray<CompareTableColumn<TRow>>;
    height?: "auto" | number;
    stickyHeader?: boolean;
};

type DeltaResult =
    | { kind: "no-change" }
    | { kind: "significant"; text: string; intent: Intent };

const computeDelta = (
    a: number,
    b: number,
    format: DeltaFormat,
    formatAbsolute: (v: number) => string
): DeltaResult => {
    if (!isRelativeDeltaSignificant(a, b)) {
        return { kind: "no-change" };
    }
    const delta = b - a;
    const sign = delta > 0 ? "+" : "−";
    const intent: Intent = delta < 0 ? Intent.SUCCESS : Intent.DANGER;
    const absoluteText = `${sign}${formatAbsolute(Math.abs(delta))}`;
    if (a === 0 || format === "absolute") {
        // Percent change is undefined when starting from zero, and a caller
        // may explicitly want the absolute change only.
        return { kind: "significant", text: absoluteText, intent };
    }
    const pct = (delta / Math.abs(a)) * 100;
    const pctText = `${sign}${Math.abs(pct).toFixed(1)}%`;
    return {
        kind: "significant",
        text: format === "both" ? `${absoluteText} (${pctText})` : pctText,
        intent
    };
};

export const CompareDeltaTag: React.FC<{
    a: number | null;
    b: number | null;
    format?: DeltaFormat;
    formatAbsolute?: (v: number) => string;
}> = ({ a, b, format = "percent", formatAbsolute = String }) => {
    if (a == null || b == null) {
        return (
            <Tag minimal round>
                —
            </Tag>
        );
    }
    const result = computeDelta(a, b, format, formatAbsolute);
    if (result.kind === "no-change") {
        return (
            <Tag minimal round>
                No change
            </Tag>
        );
    }
    return (
        <Tag minimal round intent={result.intent}>
            {result.text}
        </Tag>
    );
};

const TextCompareCellRenderer = <TRow,>(
    params: ICellRendererParams<MergedRow<TRow>> & {
        textCompareCol: TextCompareColumn<TRow>;
    }
): JSX.Element | null => {
    const data = params.data;
    if (!data) {
        return null;
    }
    const { a, b, onlyIn } = data;
    if (onlyIn === "a") {
        return (
            <span className={css["delta-text-muted"]}>
                <i>
                    <strong>Only in A</strong>
                </i>
            </span>
        );
    }
    if (onlyIn === "b") {
        return (
            <span className={css["delta-text-muted"]}>
                <i>
                    <strong>Only in B</strong>
                </i>
            </span>
        );
    }
    if (!a || !b) {
        return null;
    }
    const aVal = params.textCompareCol.getValue(a);
    const bVal = params.textCompareCol.getValue(b);
    const same = aVal === bVal;
    const tooltip = (
        <div className={css["text-compare-tooltip"]}>
            <div>
                <strong>A:</strong> {aVal || "—"}
            </div>
            <div>
                <strong>B:</strong> {bVal || "—"}
            </div>
        </div>
    );
    return (
        <Tooltip content={tooltip} minimal>
            {same ? (
                <Tag minimal round>
                    No change
                </Tag>
            ) : (
                <Tag minimal round intent={Intent.PRIMARY}>
                    Changed
                </Tag>
            )}
        </Tooltip>
    );
};

const DeltaCellRenderer = <TRow,>(
    params: ICellRendererParams<MergedRow<TRow>> & {
        compareCol: CompareColumn<TRow>;
    }
): JSX.Element | null => {
    const data = params.data;
    if (!data) {
        return null;
    }
    const { a, b, onlyIn } = data;
    if (onlyIn === "a") {
        return (
            <span className={css["delta-text-muted"]}>
                <i>
                    <strong>Only in A</strong>
                </i>
            </span>
        );
    }
    if (onlyIn === "b") {
        return (
            <span className={css["delta-text-muted"]}>
                <i>
                    <strong>Only in B</strong>
                </i>
            </span>
        );
    }
    const aVal = a ? params.compareCol.getValue(a) : null;
    const bVal = b ? params.compareCol.getValue(b) : null;
    return (
        <CompareDeltaTag
            a={aVal}
            b={bVal}
            format={params.compareCol.deltaFormat}
        />
    );
};

const combineClasses = (...cls: (string | undefined)[]): string[] =>
    cls.filter((c): c is string => Boolean(c));

const buildAgGridColumns = <TRow,>(
    columns: ReadonlyArray<CompareTableColumn<TRow>>
): (ColDef<MergedRow<TRow>> | ColGroupDef<MergedRow<TRow>>)[] => {
    const lastIdx = columns.length - 1;
    return columns.map((col, idx) => {
        const isLast = idx === lastIdx;
        const trailing = isLast ? css["trailing"] : undefined;
        if (col.kind === "join") {
            return {
                colId: col.colId,
                headerName: col.headerName,
                width: !col.flex ? col.width : undefined,
                flex: col.flex,
                headerClass: combineClasses(trailing),
                cellClass: combineClasses(trailing),
                valueGetter: ({ data }: { data?: MergedRow<TRow> }) => {
                    if (!data) {
                        return null;
                    }
                    const rep = data.a ?? data.b;
                    return rep ? col.getValue(rep) : null;
                },
                tooltipValueGetter: col.tooltip
                    ? (params) =>
                          params.value == null ? "" : String(params.value)
                    : undefined
            };
        }
        if (col.kind === "display") {
            return {
                colId: col.colId,
                headerName: col.headerName,
                width: col.width,
                flex: col.flex,
                headerClass: combineClasses(trailing),
                cellClass: combineClasses(trailing),
                valueGetter: ({ data }: { data?: MergedRow<TRow> }) => {
                    if (!data) {
                        return null;
                    }
                    const rep = data.a ?? data.b;
                    return rep ? col.getValue(rep) : null;
                },
                cellRenderer: col.cellRenderer,
                tooltipValueGetter: col.tooltip
                    ? (params) =>
                          params.value == null ? "" : String(params.value)
                    : undefined
            };
        }
        if (col.kind === "compare-text") {
            return {
                colId: col.colId,
                headerName: col.headerName,
                width: col.width,
                flex: col.flex,
                headerClass: combineClasses(trailing),
                cellClass: combineClasses(trailing),
                // Sort by changed-ness: rows where A and B differ sort above
                // unchanged rows.
                valueGetter: ({ data }: { data?: MergedRow<TRow> }) => {
                    if (!data || !data.a || !data.b) {
                        return null;
                    }
                    return col.getValue(data.a) === col.getValue(data.b)
                        ? 0
                        : 1;
                },
                cellRenderer: TextCompareCellRenderer,
                cellRendererParams: {
                    textCompareCol: col
                }
            };
        }
        // compare
        const aChild: ColDef<MergedRow<TRow>> = {
            colId: `${col.colId}__a`,
            headerName: "A",
            headerClass: combineClasses(
                css["compare-header-a"],
                css["trailing"]
            ),
            cellClass: combineClasses(css["trailing"]),
            width: !col.flex ? 75 : undefined,
            minWidth: col.flex ? 75 : undefined,
            maxWidth: col.flex ? 150 : undefined,
            flex: col.flex,
            type: "rightAligned",
            sort: col.sort,
            valueGetter: ({ data }: { data?: MergedRow<TRow> }) => {
                if (!data) {
                    return null;
                }
                return data.a ? col.getValue(data.a) : null;
            },
            valueFormatter: col.valueFormatter
        };
        const bChild: ColDef<MergedRow<TRow>> = {
            colId: `${col.colId}__b`,
            headerName: "B",
            headerClass: combineClasses(
                css["compare-header-b"],
                css["trailing"]
            ),
            cellClass: combineClasses(css["trailing"]),
            width: !col.flex ? 75 : undefined,
            minWidth: col.flex ? 75 : undefined,
            maxWidth: col.flex ? 150 : undefined,
            flex: col.flex,
            type: "rightAligned",
            valueGetter: ({ data }: { data?: MergedRow<TRow> }) => {
                if (!data) {
                    return null;
                }
                return data.b ? col.getValue(data.b) : null;
            },
            valueFormatter: col.valueFormatter
        };
        const deltaChild: ColDef<MergedRow<TRow>> = {
            colId: `${col.colId}__delta`,
            headerName: "Δ",
            headerClass: combineClasses(css["compare-header-delta"], trailing),
            cellClass: combineClasses(trailing),
            width: 100,
            type: "rightAligned",
            valueGetter: ({ data }: { data?: MergedRow<TRow> }) => {
                if (!data || !data.a || !data.b) {
                    return null;
                }
                const aVal = col.getValue(data.a);
                const bVal = col.getValue(data.b);
                if (aVal == null || bVal == null) {
                    return null;
                }
                if (aVal === 0) {
                    // Sort rows with a==0 to the extremes; bVal sign decides which end.
                    if (bVal === 0) {
                        return 0;
                    }
                    return bVal > 0
                        ? Number.POSITIVE_INFINITY
                        : Number.NEGATIVE_INFINITY;
                }
                return (bVal - aVal) / Math.abs(aVal);
            },
            cellRenderer: DeltaCellRenderer,
            cellRendererParams: {
                compareCol: col
            }
        };
        const children: ColDef<MergedRow<TRow>>[] = col.hideDelta
            ? [aChild, bChild]
            : [aChild, bChild, deltaChild];
        return {
            headerName: col.headerName,
            headerClass: combineClasses(css["compare-group-header"], trailing),
            children
        };
    });
};

const ROW_CLASS_RULES: RowClassRules<MergedRow<unknown>> = {
    [css["only-in-a"]]: ({ data }) => data?.onlyIn === "a",
    [css["only-in-b"]]: ({ data }) => data?.onlyIn === "b"
};

export const CompareTable = <TRow,>({
    merged,
    columns,
    height,
    stickyHeader
}: CompareTableProps<TRow>): JSX.Element => {
    const columnDefs = useMemo(() => buildAgGridColumns(columns), [columns]);

    return (
        <div className={css["compare-table-wrap"]}>
            <BaseAgGrid<MergedRow<TRow>>
                loadingData={[...merged]}
                columnDefs={columnDefs}
                rowClassRules={
                    ROW_CLASS_RULES as RowClassRules<MergedRow<TRow>>
                }
                getRowId={(params) => params.data.key}
                height={height}
                stickyHeader={stickyHeader}
                rowHeight={52}
            />
        </div>
    );
};
